import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TextField, Input, Button, Typography, CircularProgress } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowBack';
import RestoreIcon from '@material-ui/icons/Restore';
import api from '../../../../services/api';
import Swal from 'sweetalert2';
import { Action, Resource } from '../../../../utils/aclUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
        width: '100%',
      },
    },
    grid: {
      display: 'flex',
      '@media screen and (max-width: 991px)': {
        display: 'block',
      },
      '& > *': {
        margin: theme.spacing(1),
        width: '100%',
        '@media screen and (max-width: 991px)': {
          width: '100%',
        },
      },
    },
    button: {
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    muted: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(2, 0),
    },
  }),
);

interface ParamTypes {
  id: string;
}

function formatDate(value?: string | Date) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

let aclItem: { resource: Resource; action: Action[] | '*' } | undefined;
if (localStorage.getItem('acl')) {
  const aclString = localStorage.getItem('acl');
  const acl: { resource: Resource; action: Action[] | '*' }[] = JSON.parse(aclString!);
  aclItem = acl.find((item) => item.resource === 'Blog');
}

export default function BlogRestaurar() {
  const params = useParams<ParamTypes>();
  const history = useHistory();
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [date, setDate] = useState('');
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const id = params.id;
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const response = await api.get(`/blogs/${id}`);
        if (response.status && response.status.toString()[0] === '2') {
          const data = await response.json();
          setTitle(data.title ?? '');
          setDescription(data.description ?? '');
          setLink(data.link ?? '');
          setDate(formatDate(data.date));
          setEnabled(data.enabled);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [params.id]);

  const handleRestore = () => {
    if (!params.id) return;
    Swal.fire({
      title: 'Restaurar este post?',
      text: 'Ele voltará a aparecer na listagem principal (ativo).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#418107',
      confirmButtonText: 'Sim, restaurar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.isConfirmed) return;
      (async () => {
        try {
          const res = await api.post(`/blogs/restore/${params.id}`);
          if (res.status && res.status.toString()[0] === '2') {
            Swal.fire({
              title: 'Post restaurado',
              icon: 'success',
            }).then(() => history.push('/blogs-inativos'));
          } else {
            Swal.fire({ title: 'Não foi possível restaurar', icon: 'error' });
          }
        } catch {
          Swal.fire({ title: 'Não foi possível restaurar', icon: 'error' });
        }
      })();
    });
  };

  if (loading) {
    return (
      <main>
        <div className={classes.muted}>
          <CircularProgress size={28} />
          <Typography>Carregando…</Typography>
        </div>
      </main>
    );
  }

  if (notFound) {
    return (
      <main>
        <div className="cadastrar">
          <div className="grid-box grid-one">
            <div className="card">
              <div className="card-body">
                <Typography variant="h6">Post não encontrado.</Typography>
                <div className={classes.button}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<BackIcon />}
                    onClick={() => history.push('/blogs-inativos')}
                  >
                    Voltar aos inativos
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const canRestore =
    aclItem && (aclItem.action === '*' || aclItem.action.includes('UPDATE'));
  const alreadyActive = enabled === true;

  return (
    <main>
      <div className="cadastrar">
        <div className="grid-box grid-one">
          <div className="card">
            <div className="card-body">
              <div>
                <h1>Restaurar post do blog</h1>
                <Typography color="textSecondary" paragraph>
                  Post excluído fica inativo (<code>enabled: false</code>). Restaurar reativa o cadastro.
                </Typography>
                {alreadyActive && (
                  <Typography color="primary">Este post já está ativo na listagem principal.</Typography>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Input type="hidden" name="id" value={params.id} />
                  <div className={classes.grid}>
                    <TextField disabled label="Título" variant="outlined" value={title} fullWidth />
                  </div>
                  <div className={classes.grid}>
                    <TextField
                      disabled
                      label="Descrição"
                      variant="outlined"
                      value={description}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </div>
                  <div className={classes.grid}>
                    <TextField disabled label="Link" variant="outlined" value={link} fullWidth />
                  </div>
                  <div className={classes.grid}>
                    <TextField disabled label="Data" variant="outlined" value={date} fullWidth />
                  </div>
                  <div className={classes.grid}>
                    <TextField
                      disabled
                      label="Situação"
                      variant="outlined"
                      value={alreadyActive ? 'Ativo' : 'Inativo (excluído)'}
                      fullWidth
                    />
                  </div>
                  <div className={classes.button}>
                    {!alreadyActive && canRestore && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<RestoreIcon />}
                        onClick={handleRestore}
                      >
                        Restaurar post
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<BackIcon />}
                      onClick={() => history.push('/blogs-inativos')}
                    >
                      Voltar aos inativos
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
